'use client';

import { useEffect, useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import Icon from './Icon';

function SortableList(props: { items: SortableItemType[]; onDragEnd: any }) {
    const [items, setItems] = useState(props.items);
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    useEffect(() => {
        setItems(props.items);
    }, [props.items]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col">
                    {items.map(item => (
                        <SortableItem key={item.id} {...item} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );

    function handleDragEnd(event: any) {
        const { active, over } = event;
        if (active.id === over.id) return;

        setItems(items => {
            const oldIndex = items.findIndex(item => item.id === active.id);
            const newIndex = items.findIndex(item => item.id === over.id);
            const movedItems = arrayMove(items, oldIndex, newIndex);
            props.onDragEnd(movedItems.map(item => item.id));

            return movedItems;
        });
    }
}

type SortableItemType = {
    id: number | string;
    content: React.ReactNode;
    hasHandle?: boolean;
};
function SortableItem(props: SortableItemType) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: props.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    attributes.role = props.hasHandle ? 'generic' : 'button';

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...(!props.hasHandle ? listeners : undefined)}
            className="flex items-center border-b py-5 last:border-b-0"
        >
            {props.hasHandle && (
                <div className="move cursor-grab px-3" {...listeners}>
                    <Icon name="move" className="text-blue-300" />
                </div>
            )}
            {props.content}
        </div>
    );
}

export default SortableList;
